import dayjs from "dayjs";
import {TFunction} from "i18next";
import {CompanyStatusEventDTO, CompanyStatusEventType} from "@/api/data-contracts";
import {AdminI18N} from "@/translations";

/**
 * Builds the finished short-info line of one company status event.
 *
 * Order and filtering of the events are decided in the backend — this module only formats
 * what it receives and never drops a delivered event other than one that carries no type, no
 * timestamp, or a type this bundle does not (yet) know, none of which can be rendered at all.
 *
 * @param event - The status event as delivered in a row of the company overview.
 * @param t - The i18next translation function resolving the event message key.
 * @param dateFormat - The dayjs format string for the date, without a time part.
 * @returns The translated line, or an empty string when type or timestamp is missing or the
 *          type is unknown to this bundle.
 */
export const companyStatusEventText = (event: CompanyStatusEventDTO, t: TFunction, dateFormat: string): string => {
    const { type, occurredAt } = event;
    if (!type || !occurredAt) {
        return "";
    }
    const date = dayjs(occurredAt).format(dateFormat);
    const actorName = event.actorName?.trim();

    switch (type) {
        case CompanyStatusEventType.INVITED:
            return actorName
                ? t(AdminI18N.companies.statusEvent.invited, { date, name: actorName })
                : t(AdminI18N.companies.statusEvent.invitedWithoutActor, { date });
        case CompanyStatusEventType.REGISTERED:
            return t(AdminI18N.companies.statusEvent.registered, { date });
        case CompanyStatusEventType.CONFIRMED:
            return actorName
                ? t(AdminI18N.companies.statusEvent.confirmed, { date, name: actorName })
                : t(AdminI18N.companies.statusEvent.confirmedWithoutActor, { date });
        case CompanyStatusEventType.TERMINATED:
            return t(AdminI18N.companies.statusEvent.terminatedAutomatic, { date });
    }
    // No `default:` above on purpose: a future event type added to the union must still fail
    // this compile-time check instead of silently falling through at runtime.
    (type) satisfies never;
    return "";
};
